import { AddRoundedIcon } from 'src/app/components/Icons';
import { ActionButton, Alert, Button, Content, ContentHeader, DialogRef, FilterMenu, InputSearch, Paging, QueryState, QueryStateContent, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography } from 'src/app/components';
import { usePermissions } from 'src/app/hooks/permissions-hook';
import dayjs from 'dayjs';
import { useEffect, useRef, useState } from 'react';
import { Refs } from 'src/app/utils/refs-utils';
import { getErrorMessage } from 'src/app/utils/api-utils';
import { SalesPromoFormProps } from './SalesPromoForm.types';
import { SalesPromoModel, SalesPromoPrizeModel } from '../../../models/SalesPromo.model';
import { FilterMenuPrizeList } from '../LuckyDrawForm/FilterMenuPrizeList/FilterMenuPrizeList.component';
import SalesPromoAddPrizePopup from './AddPrizePopup/SalesPromoAddPrizePopup';
import { useSalesPromoForm2 } from './SalesPromoForm2.hook';
import { translatePrizeTypeSalesPromo } from '../SalesPromoDetail/SalesPromoDetailTabGeneral';

export function SalesPromoForm2(props: Readonly<SalesPromoFormProps>) {

    const permission = usePermissions()
    const state = useSalesPromoForm2(props.id as string);
    const addPrizeRef = useRef<DialogRef>(null);
    const deleteAlertRef = useRef<DialogRef>(null);
    const [itemToDelete, setItemToDelete] = useState<{id?: string, type?: 'rewards'|'product'}>();
    const [itemToEdit, setItemToEdit] = useState<{id?:string, type?: 'rewards'|'product', status?: string, data?: SalesPromoPrizeModel}>({})
    const clickEditPrizeItem = (item: SalesPromoPrizeModel)=> {
        const type = item.type?.toUpperCase()
        setItemToEdit({id: item.id, type: type==='PRODUCT'?'product':'rewards', status: props.currentDetail?.status, data:item})
        addPrizeRef.current?.show()
    }

    const [salesPromoStatus, setSalesPromoStatus] = useState('')
    const isEdit = (salesPromoStatus === 'PENDING' || salesPromoStatus === 'ACTIVE')
    const saveAndExit = () => {
        props.doPreventExit && props.doPreventExit(false);
        saveAsDraft('STAY');
    }

    const popupSaveAsDraftRef = useRef<DialogRef>(null);
    function saveAsDraft(key: string) {
        popupSaveAsDraftRef.current?.hide();
        const payload: SalesPromoModel = {
            id: props.id,
            is_draft: true,
        }
        const { status } = props.currentDetail!;
        const isctive = status === 'ACTIVE';
        if (!isctive) { hitPostAPI(payload, key) } else {
            Refs.pageToast.show({
                severity: 'success',
                message: "Sales Promotion successfully edited."
            });
        }
    }

    const hitPostAPI = async (payload: SalesPromoModel, key: string) => {
        try {
            const result = await state.saveSalesPromoGeneral.mutateAsync(payload)
            const response_output = result.data.response_output
            const contentDetail = response_output?.detail
            if (key === 'SUBMIT') {
                props.save && props.save(contentDetail)
            } else {
                props.saveDraft && props.saveDraft(key)
            }
        } catch (error: any) {
            if (error?.response?.data?.response_schema?.response_code === 'PAKUWON-400')
                Refs.pageToast.show({
                    severity: 'danger',
                    message: `${getErrorMessage(error, true)?.en}`
                });
        }
    }

    const clickDeletePrizeItem = (item:SalesPromoPrizeModel) => { 
        const type = item.type === 'PRODUCT' ? 'product' : 'rewards';
        setItemToDelete({id: item.id, type}); 
        deleteAlertRef.current?.show();
    }

    const handleDelete = () => {
        const payload = { campaign_id: props.id as string, prize_id: itemToDelete?.id as string, prize_type: itemToDelete?.type as 'rewards'|'product' }
        state.deletePrize.mutate(payload, {
            onSuccess: () => {
                deleteAlertRef.current?.hide()
            },
        });
    };

    useEffect(() => {
        setSalesPromoStatus(props.currentDetail?.status ?? '');
    }, [props.currentDetail])

    const renderItem = (item: SalesPromoPrizeModel, index: number) => {
        return (
            <TableRow key={item.id}>
                <TableCell {...{ index }} />
                <TableCell value={translatePrizeTypeSalesPromo(item?.type)} />
                <TableCell value={item?.name} />
                <TableCell value={item?.quantity} />
                <TableCell value={item?.remaining_quantity} />
                <TableCell
                    renderValue={(date: string) => {
                        return (
                            <div>
                                {dayjs(date).format('DD/MM/YYYY')}
                            </div>
                        )
                    }}
                    className="w-[145px]"
                    value={item?.valid_until}
                />
                {salesPromoStatus !== 'INACTIVE' && (
                    <TableCell action={true}>
                        <Stack direction="row" gap={2}>
                            {permission.setVisibleByAccess(
                                ['manage_campaign_sales_promo.create', 'manage_campaign_sales_promo.update'],
                                <>
                                    <ActionButton variant="edit" onClick={() => (
                                        clickEditPrizeItem(item)
                                    )} />
                                    {salesPromoStatus !== 'ACTIVE' && (
                                        <ActionButton
                                            variant="delete" color="primary"
                                            onClick={()=>(clickDeletePrizeItem(item))}
                                            disabled={state.prizeList.data?.content?.length === 1 && salesPromoStatus === 'PENDING'}
                                        />
                                    )}
                                </>
                            )}
                        </Stack>
                    </TableCell>
                )}
            </TableRow>
        )
    }


    return (
        <>
            <Stack className='px-8 py-6'>
                <p className="text-xs text-gray-400 dark:text-gray-500">{props.currentDetail?.name ?? '-'}</p>
                <Content>
                    <ContentHeader title="Prize List">
                        {permission.setVisibleByAccess(
                            ['manage_campaign_sales_promo.create', 'manage_campaign_sales_promo.update'],
                            <Button
                                startIcon={AddRoundedIcon}
                                onClick={() => { addPrizeRef.current?.show() }}
                            >
                                Add Prize
                            </Button>,
                        )}
                    </ContentHeader>

                    <Stack
                        direction="row"
                        justify="between"
                        items="center"
                        className="px-6 py-4"
                    >
                        <InputSearch />
                        <FilterMenu>
                            <FilterMenuPrizeList isSalesPromoPage={true} />
                        </FilterMenu>
                    </Stack>

                    <QueryState query={state.prizeList}>
                        <div className="px-8">
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell index />
                                        <TableCell>Prize Type</TableCell>
                                        <TableCell>Prize Name</TableCell>
                                        <TableCell>Prize Qty</TableCell>
                                        <TableCell>Remaining Prize Qty</TableCell>
                                        <TableCell className="w-[145px]">Valid Until</TableCell>
                                        <TableCell action={true} className="w-[100px]" />
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {state.prizeList.data?.content?.map(renderItem)}
                                </TableBody>
                            </Table>
                            <Paging query={state.prizeList} />
                        </div>
                        <QueryStateContent title="List Prize is Empty" description="Click 'Add' button to create new entry" />
                    </QueryState>
                </Content>
            </Stack>
            {permission.setVisibleByAccess(
                ['manage_campaign_sales_promo.create', 'manage_campaign_sales_promo.update'],
                <Stack
                    className={'px-8 py-6 border-t border-neutral-200'}
                    direction="row"
                    gap={4}
                >
                    <Button variant='outlined' loading={state.saveSalesPromoGeneral.isLoading} onClick={() => { isEdit ? saveAndExit() : popupSaveAsDraftRef.current?.show() }}>{'Save' + (!isEdit ? ' as Draft' : '')}</Button>
                    <Stack
                        direction="row"
                        justify='end'
                        gap={4}
                    >
                        <Button variant='outlined' loading={state.saveSalesPromoGeneral.isLoading} onClick={() => { props.backward && props.backward() }}>Previous</Button>
                        <Button loading={state.saveSalesPromoGeneral.isLoading} onClick={() => { props.save && props.save(props.currentDetail) }} disabled={!state.prizeList.data || !state.prizeList.data?.content || state.prizeList.data?.content?.length < 1}>Next</Button>
                    </Stack>
                </Stack>
            )}
            <SalesPromoAddPrizePopup dialogRef={addPrizeRef} itemToEdit={itemToEdit} cancel={()=>(setItemToEdit({}))} {...props}></SalesPromoAddPrizePopup>
            <Alert
                cancelText="Go to List"
                submitText="Stay on page"
                onCancel={() => saveAsDraft('BACK')}
                onSubmit={() => saveAsDraft('STAY')}
                title="Save as Draft?"
                ref={popupSaveAsDraftRef}
                closeOnBackdropClicked={false}
            >
                <Typography>
                    Your progress will be saved as a draft. You can continue editing later.
                    <br />Would you like to stay on this page or return to the list?
                </Typography>
            </Alert>
            <Alert
                cancelText="Cancel"
                submitText="Delete"
                onSubmit={handleDelete}
                title="Delete Prize?"
                ref={deleteAlertRef}
                closeOnBackdropClicked={false}
            >
                <Typography>
                    Are you sure want to delete this Prize? You can create again later.
                </Typography>
            </Alert>
        </>
    )
}
